import { Provider as JotaiProvider } from 'jotai';
import { createStore } from 'jotai';
import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';

import 'unfonts.css';
import './index.css';

import {
  createBrowserRouter,
  Params,
  redirect,
  RouterProvider,
} from 'react-router-dom';

import { PhoneAtom as DLPhoneAtom } from './apps/DirectLink/lib/state.ts';
import { AuthAtom, PhoneAtom as FLPhoneAtom } from './apps/Frictionless/lib/state.ts';
import { SignatureAtom } from './apps/CancelLink/lib/state.ts';

import Config from './lib/Config.ts';
import { getPhone } from './lib/getPhone.ts';
import { initSentry } from './lib/sentry.ts';

import Iframe from './apps/Iframe/Iframe.tsx';

import CancelLink from './apps/CancelLink/CancelLink.tsx';
import CLConfirm from './apps/CancelLink/Confirm/Confirm.tsx';

import BookingUrl from './apps/BookingUrl/BookingUrl.tsx';
import BookingUrlIndex from './apps/BookingUrl/Index/Index.tsx';
import BookingUrlBooking from './apps/BookingUrl/Booking/Booking.tsx';

import Widget from './apps/Widget/Widget.tsx';
import WidgetIndex from './apps/Widget/Index/Index.tsx';
import { widgetChildRoutes } from './apps/Widget/routes.tsx';
import EmbeddedPaymentFrame from './apps/Widget/Payment/EmbeddedPaymentFrame.tsx';

import DirectLink from './apps/DirectLink/DirectLink.tsx';
import DLPhone from './apps/DirectLink/Phone/Phone.tsx';
import DLAuth from './apps/DirectLink/Auth/Auth.tsx';
import DLInfo from './apps/DirectLink/Info/Info.tsx';
import DLAccept from './apps/DirectLink/Accept/Accept.tsx';
import DLPayment from './apps/DirectLink/Payment/Payment.tsx';
import DLOne01 from './apps/DirectLink/One01/One01.tsx';
import DLConfirm from './apps/DirectLink/Confirm/Confirm.tsx';
import DLConfirmation from './apps/DirectLink/Confirmation/Confirmation.tsx';
import DLExpired from './apps/DirectLink/Expired/Expired.tsx';
import DLUnavailable from './apps/DirectLink/Unavailable/Unavailable.tsx';
import DLProcessingPayment from './apps/DirectLink/ProcessingPayment/ProcessingPayment.tsx';
import DLCanceled from './apps/CancelLink/Canceled/Canceled.tsx';
import DLNotFound from './apps/CancelLink/NotFound/NotFound.tsx';
import { Guest } from './apps/DirectLink/Guest/Guest.tsx';

import Frictionless from './apps/Frictionless/Frictionless.tsx';
import FLPhone from './apps/Frictionless/Phone/Phone.tsx';
import FLAuth from './apps/Frictionless/Auth/Auth.tsx';
import FLDetails from './apps/Frictionless/Details/Details.tsx';
import FLCustomTip from './apps/Frictionless/CustomTip/CustomTip.tsx';

import { AuthResponse } from './lib/types.ts';
import ConciergeMember from '@/apps/Frictionless/Concierge/ConciergeMember.tsx';

import Home from './apps/Home/Home.tsx';

const myStore = createStore();

Config.setFromFile()
  .then(() => {
    initSentry('spa');

    const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouter(createBrowserRouter);

    const routes = [
      {
        path: 'reservation/:uuid',
        element: <DirectLink />,
        children: [
          {
            path: '',
            loader: ({ request }: { request: Request }) => {
              const json = localStorage.getItem('dl_auth'); // atomWithStorage, but load it directly to skip the holding pattern while it loads
              const urlParams = new URL(request.url).searchParams;

              const auth: AuthResponse | null = json ? (JSON.parse(json) as AuthResponse) : null;

              if (auth?.token || urlParams.get('skip_auth')) {
                return redirect('./accept');
              }

              const number = urlParams.get('phone');
              if (number) {
                try {
                  const phone = getPhone(number);
                  if (phone) {
                    myStore.set(DLPhoneAtom, phone);
                    return redirect('./auth');
                  }
                } catch (e) {
                  console.error(e);
                }
              }

              return redirect('./phone');
            },
          },
          {
            path: 'phone',
            element: <DLPhone />,
          },
          {
            path: 'auth',
            element: <DLAuth />,
          },
          {
            path: 'info',
            element: <DLInfo />,
          },
          {
            path: 'accept',
            element: <DLAccept />,
          },
          {
            path: 'payment',
            element: <DLPayment />,
          },
          {
            path: '101',
            element: <DLOne01 />,
          },
          {
            path: 'confirm',
            element: <DLConfirm />,
          },
          {
            path: 'confirmation',
            element: <DLConfirmation />,
          },
          {
            path: 'expired',
            element: <DLExpired />,
          },
          {
            path: 'unavailable',
            element: <DLUnavailable />,
          },
          {
            path: 'processing_payment',
            element: <DLProcessingPayment />,
          },
          {
            path: 'guests',
            element: <Guest />,
          },
        ],
      },
      {
        path: 'concierge-reservation/:uuid?/*',
        loader: ({ request, params }: { request: Request; params: Params }) => {
          const searchParams = new URL(request.url).searchParams;
          const token = searchParams.get('token');
          const uuid = params.uuid;

          if (token) {
            const auth: AuthResponse = {
              user: 'concierge_user',
              token: token,
              new: false,
              is_concierge_member: true,
            };
            myStore.set(AuthAtom, auth);
            return redirect(`../reservation/${uuid}/receipt`);
          }

          return { uuid: uuid };
        },
        element: <ConciergeMember />,
      },
      {
        path: 'reservation/:uuid/receipt',
        element: <Frictionless />,
        children: [
          {
            path: '',
            loader: ({ request }: { request: Request }) => {
              const number = new URL(request.url).searchParams.get('phone');

              // If phone is in URL, ALWAYS force new authentication
              if (number) {
                // Clear existing auth to avoid user type conflicts
                myStore.set(AuthAtom, null);

                try {
                  const phone = getPhone(number);
                  if (phone) {
                    myStore.set(FLPhoneAtom, phone);
                    return redirect('./auth');
                  }
                } catch (e) {
                  console.error(e);
                }
              } else {
                const json = localStorage.getItem('frictionless_auth'); // atomWithStorage, but load it directly to skip the holding pattern while it loads
                const auth: AuthResponse | null = json ? (JSON.parse(json) as AuthResponse) : null;
                if (auth?.token) {
                  return redirect('./details');
                }
              }

              return redirect('./phone');
            },
          },
          {
            path: 'phone',
            element: <FLPhone />,
          },
          {
            path: 'auth',
            element: <FLAuth />,
          },
          {
            path: 'details',
            loader: () => {
              const json = localStorage.getItem('frictionless_auth');
              const auth: AuthResponse | null = json
                ? (JSON.parse(json) as AuthResponse)
                : null;

              if (!auth?.token) {
                return redirect('../phone');
              }

              return null;
            },
            element: <FLDetails />,
          },
          {
            path: 'tip',
            element: <FLCustomTip />,
          },
        ],
      },
      {
        path: 'cancel/:uuid',
        element: <CancelLink />,
        children: [
          {
            path: '',
            loader: ({ request, params }: { request: Request; params: Params }) => {
              const uuid = params.uuid;
              const signature = new URL(request.url).searchParams.get('sig');

              if (uuid) {
                if (signature) {
                  myStore.set(SignatureAtom, { [uuid]: signature });
                  return redirect('./confirm');
                }

                const json = localStorage.getItem('cancel_signature'); // atomWithStorage, but load it directly to skip the holding pattern while it loads
                const saved_signature: Record<string, string> | null = json
                  ? (JSON.parse(json) as Record<string, string>)
                  : null;
                if (saved_signature?.[uuid]) {
                  return redirect('./confirm');
                }
              }

              return redirect('./404');
            },
          },
          {
            path: '404',
            element: <DLNotFound />,
          },
          {
            path: 'confirm',
            element: <CLConfirm />,
          },
          {
            path: 'canceled',
            element: <DLCanceled />,
          },
        ],
      },
      {
        path: '/',
        element: <Home />,
      },
      // Dorsia-origin payment surface framed by the overlay widget's
      // EmbeddedPaymentHost. Stripe Elements can't mount in the widget's shadow
      // root, so the card step is rendered here (light DOM) and posts the
      // tokenized method back. Light-weight: no widget chrome or guards.
      {
        path: 'embed/pay',
        element: <EmbeddedPaymentFrame />,
      },
      {
        path: 'restaurant/:id',
        element: <Widget />,
        // Single source of the widget flow routes — shared with the embed
        // (src/apps/Widget/routes.tsx). Do not inline route definitions here.
        children: widgetChildRoutes,
      },
      // Redirect old /restaurant path to home
      {
        path: 'restaurant/',
        loader: () => redirect('/'),
      },
    ];

    if (Config.data.environment !== 'production') {
      routes.push({
        path: '/dev/restaurants',
        element: <WidgetIndex />,
      });

      routes.push({
        path: '/iframe',
        element: <Iframe />,
      });

      routes.push({
        path: '/booking_url',
        element: <BookingUrlIndex />,
      });

      routes.push({
        path: '/booking_url/:id',
        element: <BookingUrl />,
        children: [
          {
            path: '',
            element: <BookingUrlBooking />,
          },
        ],
      });
    }

    const router = sentryCreateBrowserRouter(routes);

    if (import.meta.hot) {
      import.meta.hot.dispose(() => router.dispose());
    }

    const app = (
      <JotaiProvider store={myStore}>
        <GoogleReCaptchaProvider
          reCaptchaKey={Config.data?.recaptcha?.site_key || ''}
          scriptProps={{ async: true, defer: true }}
        >
          <RouterProvider router={router} />
          <SpeedInsights />
        </GoogleReCaptchaProvider>
      </JotaiProvider>
    );

    ReactDOM.createRoot(document.getElementById('root')!).render(
      Config.data.environment === 'local' ? app : <React.StrictMode>{app}</React.StrictMode>
    );
  })
  .catch(() => console.error('Missing Config'));
